import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Spin, Table } from 'antd';
import Icon, { InfoCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import { intersectionBy } from 'lodash';
import moment, { Moment } from 'moment';
import deployLined from '@icons/emp/deploy.svg';
import ArrowRightOutlinedIcon from '@icons/sankey/arrow-right-outlined.svg';
import {
  useDeployCandidateList,
  useGetWorkbenchDeployAvailableInterval,
  useGetWorkbenchLatestDeployInfo,
} from '@api/emp/queryHooks';
import { EDataTransformDimensionName } from '@src/common/constant/enum';
import { SearchFilterPanel } from '@components/common/ManagementTable/components/SearchFilterPanel';
import { getSelectiveDeploymentOptions } from '@src/emp-3.0/utils/buildOptions';
import { StyledDrawer, StyledHeader, StyledSuccessText, StyledTextBtn, Footer } from './styled';
import { WorkbenchAction, IDeploymentProps } from './typed';
import { DEFAULT_VALUE } from './constants';
import DeploymentTable from './DeploymentTable';
import { extractWorkflowIdFromUrl } from './utils';
import {
  generateDateColumn,
  generateNameColumn,
  generateTypeColumn,
  generateUserColumn,
} from './generateColumn';
import { generateDeploymentTableFilterData } from './searchFilterData';
import { useNavigate } from 'react-router-dom';
import DatePickerWithOptions from '@src/components/summary-panel/SummaryContainer/DatePickerWithOptions';

const warningColor = '#fa8c16';

/**
 * The end_time of interval is from Control Plane,
 * For safety considerations, we have moved the end time 12 hours earlier.
 * As the minimum granularity of Date picker is day, so move forward one day.
 */
function adjustAvailableInterval(interval?: { start_time: number; end_time: number }) {
  if (!interval) {
    return interval;
  }
  const { start_time, end_time } = interval;
  const endDt = moment(end_time - 12 * 3600000);
  const fixedEndTime = endDt.subtract(1, 'day').endOf('day').valueOf();
  return {
    start_time,
    end_time: fixedEndTime,
  };
}
/**
 * As emp server only support half/whole hour,
 * this function align the time to latest half/whole hour
 */
function adjustDatetime(dt: Moment, availableInterval?: { start_time: number; end_time: number }) {
  if (availableInterval) {
    const start = moment(availableInterval.start_time);
    const end = moment(availableInterval.end_time);
    if (dt.isBefore(start)) {
      dt = start;
    } else if (dt.isAfter(end)) {
      dt = end;
    }
  }
  const minute = dt.minute();
  dt.set('second', 0);
  if (minute === 0 || minute === 30) {
    return dt;
  }
  if (minute === 59) {
    dt = dt.add(1, 'minute');
    return dt;
  }
  if (minute > 31) {
    dt.set('minute', 30);
  } else {
    dt.set('minute', 0);
  }
  return dt;
}

export const WorkbenchDeploymentDrawer: React.FC<
  IDeploymentProps & {
    action?: WorkbenchAction;
    isDeploying?: boolean;
    isExporting?: boolean;
    onExport?: (value: any) => void;
  }
> = ({
  deploySuccess,
  drawerProps = {},
  onDeploy,
  onExport,
  metricType,
  enableSecondPanel,
  action = WorkbenchAction.deployWorkBench,
  isDeploying,
  isExporting,
}) => {
  const url = window.location.href;
  const id = extractWorkflowIdFromUrl(url);
  const topRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { open, onClose } = drawerProps;

  const [messageApi, contextHolder] = message.useMessage();
  const {
    data: deployListData,
    isFetching: isFetchingDeployList,
    refetch: refetchCandidateList,
  } = useDeployCandidateList();
  const { data: deployInfo } = useGetWorkbenchLatestDeployInfo();
  const { data: originAvailableInterval, isFetching: isFetchingDeployAvailableInterval } =
    useGetWorkbenchDeployAvailableInterval();
  const availableInterval = useMemo(
    () => adjustAvailableInterval(originAvailableInterval),
    [originAvailableInterval],
  );
  const [interval, setInterval] = useState<[Moment, Moment]>();
  useEffect(() => {
    if (availableInterval) {
      setInterval([
        moment(availableInterval.end_time).subtract(1, 'day'),
        moment(availableInterval.end_time),
      ]);
    }
  }, [availableInterval]);

  const isRequestingDeploy = action === WorkbenchAction.deployWorkBench && isDeploying;
  const isRequestingExport = action === WorkbenchAction.exportToProduction && isExporting;
  const showLoadingForDeploy =
    (action === WorkbenchAction.deployWorkBench &&
      (isFetchingDeployAvailableInterval ||
        deployInfo?.status === 'running' ||
        deployInfo?.status === 'pending')) ||
    isRequestingDeploy;
  const showLoading = isFetchingDeployList || showLoadingForDeploy || isRequestingExport;

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  // deployment filters
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [deploymentType, setDeploymentType] = useState<string>(DEFAULT_VALUE);
  const [updatedBy, setUpdatedby] = useState<string>(DEFAULT_VALUE);
  const [updateDate, setUpdateDate] = useState<Moment[]>([]);
  const [sort, setSort] = useState<{ key: string; order: number }>({ key: '', order: 0 });

  const enableSuccessPanel = enableSecondPanel ? deploySuccess : false;

  const { typeOptions, updatedByOptions } = getSelectiveDeploymentOptions(
    deployListData?.data,
    'All',
  );

  const dataSource = useMemo(() => {
    const {
      data: resultData,
      selectedRowKeys: defaultSelectedRowKeys,
      selectedRows: defaultSelectedRows,
    } = generateDeploymentTableFilterData(
      deployListData?.data,
      {
        searchText,
      },
      {
        type: deploymentType,
        updatedBy,
        updateDate,
      },
      sort,
      id,
    );

    if (action === WorkbenchAction.deployWorkBench && Array.isArray(resultData)) {
      for (const it of resultData) {
        if (it.is_deployed) {
          defaultSelectedRowKeys.push(it.id);
          defaultSelectedRows.push(it);
        }
      }
    }
    if (defaultSelectedRows.length > 0) {
      setSelectedRowKeys(defaultSelectedRowKeys);
      setSelectedRows(defaultSelectedRows);
    } else {
      setSelectedRows([]);
      setSelectedRowKeys([]);
    }
    // this can be uncommented in later release.
    // if (action === WorkbenchAction.exportToProduction && Array.isArray(resultData)) {
    //   return resultData.filter((item) => !item.need_hidden_in_export);
    // }

    return resultData;
  }, [
    deployListData?.data,
    searchText,
    deploymentType,
    updatedBy,
    updateDate,
    sort,
    id,
    action,
    open,
  ]);

  const selectedRowsAfterFilter = useMemo(
    () => intersectionBy(selectedRows, dataSource, 'id'),
    [dataSource, selectedRows],
  );

  const hasSelectedRows = useMemo(
    () => selectedRowsAfterFilter.length > 0,
    [selectedRowsAfterFilter.length],
  );
  const isDeployListAvailable = deployListData?.data?.length > 0;
  useEffect(() => {
    if (open && !isFetchingDeployList && !isDeployListAvailable) {
      messageApi.info({
        content:
          'There are no updates to deploy right now. Feel free to create some new metrics or dimensions!',
        icon: <InfoCircleTwoTone />,
      });
      if (!enableSuccessPanel) {
        // @ts-ignore
        onClose?.();
      }
    }
  }, [open, isDeployListAvailable, isFetchingDeployList, messageApi, enableSuccessPanel]);

  const handleDeployClick = () => {
    const initializeDeployData = () => ({
      ids: [] as number[],
      deploy_all: false,
      flag_to_deploy: false,
    });

    const payload: any = {
      mapped_events: initializeDeployData(),
      metrics: initializeDeployData(),
      data_transform: initializeDeployData(),
      custom_dimension: initializeDeployData(),
      flow: initializeDeployData(),
    };
    if (action === WorkbenchAction.deployWorkBench && interval) {
      payload.interval = `${adjustDatetime(
        interval[0].clone(),
        availableInterval,
      ).format()}/${adjustDatetime(interval[1].clone(), availableInterval).format()}`;
    }

    selectedRowsAfterFilter.forEach((item) => {
      const { type, id } = item;
      if (payload[type]) {
        payload[type].ids.push(id);
      }
    });
    if (action === WorkbenchAction.deployWorkBench) {
      onDeploy?.(payload);
    } else if (action === WorkbenchAction.exportToProduction) {
      onExport?.(payload);
    }
  };

  const resetFilters = () => {
    setDeploymentType(DEFAULT_VALUE);
    setUpdatedby(DEFAULT_VALUE);
    setUpdateDate([]);
  };

  const columns = [
    Table.SELECTION_COLUMN,
    Table.EXPAND_COLUMN,
    generateNameColumn(),
    generateTypeColumn({
      column: {
        showFilter,
        options: typeOptions,
        value: deploymentType,
        onChange: (value: string) => setDeploymentType(value),
        sortEnabled: true,
        sort,
        onSortChange: setSort,
        headerStyle: { padding: 0 },
      },
    }),
    generateUserColumn({
      column: {
        title: 'Latest updated by',
        dataKey: 'last_updated_by',
        showFilter,
        options: updatedByOptions,
        value: updatedBy,
        onChange: (value: string) => setUpdatedby(value),
        sortEnabled: true,
        sort,
        onSortChange: setSort,
      },
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
    }),
    generateDateColumn({
      column: {
        title: 'Latest updated at',
        dataKey: 'edited_time',
        showFilter,
        value: updateDate,
        onChange: setUpdateDate,
        sortEnabled: true,
        sort,
        onSortChange: setSort,
      },
      dataIndex: 'edited_time',
      key: 'edited_time',
    }),
  ];

  const modalFooter = (
    <Footer>
      {action === WorkbenchAction.deployWorkBench && (
        <div
          style={{
            color: warningColor,
          }}
        >
          Note: Deployment may take 15 minutes or longer, depending on the data volume
        </div>
      )}
      <Button onClick={onClose}>Cancel</Button>
      <Button
        type="primary"
        style={
          hasSelectedRows
            ? undefined
            : {
                color: '#fff',
                backgroundColor: '#0046ff',
                borderColor: 'transparent',
                opacity: 0.4,
              }
        }
        onClick={handleDeployClick}
        loading={isRequestingExport || isRequestingDeploy}
        disabled={showLoading || !hasSelectedRows}
      >
        {action === WorkbenchAction.deployWorkBench ? 'Deploy' : 'Export'}
      </Button>
    </Footer>
  );
  return (
    <>
      {contextHolder}
      {(isDeployListAvailable || enableSuccessPanel) && (
        <StyledDrawer
          width={enableSuccessPanel ? 600 : 1200}
          destroyOnClose
          {...drawerProps}
          title={
            <StyledHeader>
              <div>
                {action === WorkbenchAction.deployWorkBench ? (
                  <Icon component={deployLined} style={{ marginRight: '8px' }} />
                ) : (
                  <Icon component={ArrowRightOutlinedIcon} style={{ marginRight: '8px' }} />
                )}
                <span>{enableSuccessPanel ? 'Confirm Deployment' : action}</span>
              </div>
              {enableSuccessPanel ? null : (
                <SearchFilterPanel
                  style={{ height: '24px', width: 'auto' }}
                  showFilter={showFilter}
                  onFilterVisibilityChange={(value) => {
                    resetFilters();
                    setShowFilter(value);
                  }}
                  searchText={searchText}
                  onSearch={setSearchText}
                />
              )}
            </StyledHeader>
          }
          footer={enableSuccessPanel ? null : modalFooter}
        >
          {enableSuccessPanel && (
            <div style={{ padding: 16 }}>
              <StyledSuccessText>Deployment Successful!</StyledSuccessText>
              <p>
                Full traffic monitoring and drill-down analysis for the
                {selectedRows
                  .map((i) => `"${EDataTransformDimensionName[i.name] ?? i.name}"`)
                  .join(', ')}{' '}
                are now available on Pulse.
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <StyledTextBtn
                  onClick={(e) => {
                    onClose?.(e);
                    navigate(`/workflow/${metricType}/new`, { replace: true });
                  }}
                >
                  <PlusOutlined />
                  <span>{`Create another ${metricType}`}</span>
                </StyledTextBtn>
                <Button type="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
          <Spin spinning={showLoading}>
            <div
              ref={topRef}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 6,
                backgroundColor: 'white',
              }}
            >
              {action === WorkbenchAction.deployWorkBench ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '6px 0px',
                  }}
                >
                  <div>Set Historical Data Range: </div>
                  {interval && (
                    <DatePickerWithOptions
                      calenderBoundry={availableInterval}
                      granularity={30}
                      shortcuts={[]}
                      // durationLimit={durationLimit}
                      // title="Time Frame"
                      // ref={summaryDatePickerRef}
                      // expanded={comparisonOpen}
                      defaultValue={interval}
                      // placement="right"
                      // className={datePickerClassName}
                      // defaultShortcut={shortcut}
                      onApply={(range) => {
                        const [from, to] = range;
                        setInterval([from, to]);
                      }}
                    />
                  )}
                </div>
              ) : (
                <div
                  style={{
                    color: warningColor,
                    padding: '6px 0px',
                  }}
                >
                  Note: Exported assets will remain in the list of Workbench Mode until they are
                  deployed in Production Mode of Activation
                </div>
              )}
            </div>
            {!enableSuccessPanel && (
              <DeploymentTable
                tableProps={{
                  sticky: { offsetHeader: topRef.current?.offsetHeight ?? 0 },
                  columns,
                  dataSource,
                  rowSelection: {
                    selectedRowKeys: selectedRowKeys,
                    onChange: (selectedRowKeys, selectedRows, { type }) => {
                      setSelectedRowKeys(selectedRowKeys);
                      setSelectedRows(selectedRows);
                    },
                  },
                }}
              />
            )}
          </Spin>
        </StyledDrawer>
      )}
    </>
  );
};
